// Story 11.12 (ADO-1963), T3 (AC1, AC4): add a product to the store's WOW order without touching
// the order already started.
//
// The WOW order is a cart of its own (the `WOW` entry's depot and account, E11-D3: WOW and
// standard lines never combine). Everything here names that cart (`WowOfferContext.cart`) and
// nothing else: it never reads or writes `useBasketStore` (the session's active cart), and never
// changes the active entry. The only way the active entry changes is the person following the
// "View your WOW order" link afterwards (`WowOfferAction`).
//
// The flow:
//   1. `startWowAdd` reads the WOW cart first (`listBasket`), for its revision and any line the
//      product already has there.
//   2. No line: add 1 (`addToBasket` with no seen token and the revision). The server quotes and
//      locks the WOW price under the cart lock; what is confirmed is the unit price of the line
//      it returns, not the price on screen.
//   3. A line already there: nothing is written. The person is told what they have and at what
//      locked price, and offered "Add 1 more" (`addOneMoreToWow`), which sends the line's token
//      and the revision, so it increments at the price locked when the line was made (E11-D1,
//      Story 8.2 D-4), never at the price on screen.
// How an answer is classified (on the error's type and its `status` and `problemType`, never on
// its message):
//   - The first read refused with a 403: FORBIDDEN, nothing written. The server no longer lets
//     this session use that cart (the WOW entry is no longer offered to it, or its account is
//     not); the caller re-reads the offer and the ordering choices.
//   - The first read failing any other way (a network error, a 401, a 5xx, an unreadable body):
//     UNREAD, nothing written; trying again may work.
//   - Any 403 carrying a terminal problem type (`TERMINAL_PROBLEM_TYPES`): SESSION ENDED.
//     `apiFetch` has already ended the session centrally; nothing is said here.
//   - 409 `cart-line-conflict` on the write: the line changed meanwhile. The existing conflict
//     copy (`conflictView`), no automatic retry.
//   - 409 `cart-sending`: a send holds the cart; tried once more after the basket's own wait, by
//     the basket's own rule (`withOneRetryIfSending`), and only while the page is still on it.
//   - A 403 on the write: FORBIDDEN, as for the read. A 400 (`PRICE_UNRESOLVABLE`,
//     `AVAILABILITY_BLOCKED`, the server's own words) or any other 4xx: REFUSED, said so, and the
//     caller re-reads the offer.
//   - A network error, a 5xx or an unreadable success on the write: UNCERTAIN, the line may have
//     been written. Never sent again; the WOW cart is read again and what it holds is reported.

import {
  addToBasket,
  BasketHttpError,
  BasketLineConflictError,
  BasketServerError,
  CartAlreadySentError,
  CartSendingError,
  listBasket,
  type BasketLine,
  type BasketState,
} from '../api/basket.client';
import type { CartTarget } from '../api/cart-target';
import { TERMINAL_PROBLEM_TYPES } from '../api/shoplink-api';
import type { MoneyValue } from '../shared/money';
import { formatMoney } from '../shared/money';
import { conflictView } from './conflictCopy';
import { withOneRetryIfSending } from './useBasketStore';

/** The product's line on the WOW order, as the server holds it. */
export interface WowOrderLine {
  readonly quantity: number;
  /** The unit price locked on the line; absent only when the server sent none. */
  readonly unitPrice?: MoneyValue;
}

export type WowAddResult =
  /** Written: the line as the server returned it. */
  | { readonly kind: 'added'; readonly line: WowOrderLine }
  /** Nothing written: the product is already on the WOW order. "Add 1 more" sends `token`. */
  | {
      readonly kind: 'alreadyOnOrder';
      readonly line: WowOrderLine;
      readonly token: string;
      readonly revision: number;
    }
  /** Nothing written: the line changed meanwhile (the conflict copy). */
  | { readonly kind: 'conflict'; readonly message: string }
  /** Nothing written: refused (price, availability, a held or sent cart, another 4xx). */
  | { readonly kind: 'refused'; readonly message: string }
  /** Nothing written: the server refused this session the WOW cart (a 403, read or write). */
  | { readonly kind: 'forbidden' }
  /** Nothing written: the session was ended centrally (a terminal 403); nothing to say here. */
  | { readonly kind: 'sessionEnded' }
  /** The write's outcome is unknown. `line`: what the WOW order holds now (`null`: none), or
   *  `undefined` when it could not be read either. */
  | { readonly kind: 'uncertain'; readonly line: WowOrderLine | null | undefined }
  /** Nothing written: the WOW order could not be read before the add. */
  | { readonly kind: 'unread' };

/** Is the add still wanted (the page still shows this product for this WOW cart)? Checked
 *  before the one retry after a held cart. */
export type StillCurrent = () => boolean;

/** A 403 on the WOW cart: the cart is refused to this session, for whatever reason the server
 *  had (an entry no longer offered, an account no longer permitted). Not a claim about the
 *  product. */
export const WOW_FORBIDDEN_MESSAGE = "Your WOW order isn't available to you right now.";
export const WOW_REFUSED_MESSAGE = "This couldn't be added to your WOW order. Please try again.";
export const WOW_SENDING_MESSAGE = 'Your WOW order is being sent. Try again in a moment.';
export const WOW_SENT_MESSAGE = 'Your WOW order was just sent. Try again to start a new one.';
export const WOW_UNREAD_MESSAGE = "We couldn't reach your WOW order. Please try again.";

function lineOf(line: BasketLine): WowOrderLine {
  return {
    quantity: line.quantity,
    ...(line.unitPrice === undefined ? {} : { unitPrice: line.unitPrice }),
  };
}

function productLine(basket: BasketState, sku: string): BasketLine | undefined {
  return basket.lines.find((line) => line.skuCode === sku);
}

/** A status-coded refusal the session cannot outlive (deactivated, no access): `apiFetch` has
 *  already ended the session. */
function endsTheSession(error: BasketHttpError): boolean {
  return (
    error.status === 403 &&
    error.problemType !== undefined &&
    TERMINAL_PROBLEM_TYPES.includes(error.problemType)
  );
}

/** Read the WOW order again after a write whose outcome is unknown, and report what it holds. */
async function reportUncertain(sku: string, cart: CartTarget): Promise<WowAddResult> {
  try {
    const line = productLine(await listBasket(cart), sku);
    return { kind: 'uncertain', line: line === undefined ? null : lineOf(line) };
  } catch {
    return { kind: 'uncertain', line: undefined };
  }
}

/** One add of 1 to the WOW cart, from the line `seenToken` (`null`: none) at `seenRevision`. */
async function writeWowLine(
  sku: string,
  cart: CartTarget,
  seenToken: string | null,
  seenRevision: number,
  stillCurrent: StillCurrent,
): Promise<WowAddResult> {
  let basket: BasketState;
  try {
    // The basket's one-retry rule for a held cart (Story 8.3, Task 13.2): once more after the
    // wait, only while the page is still on this product and cart; otherwise the hold stands.
    basket = await withOneRetryIfSending(
      () => addToBasket(sku, cart, 1, seenToken, seenRevision),
      stillCurrent,
    );
  } catch (error) {
    if (error instanceof BasketLineConflictError) {
      const view = conflictView({
        action: 'add',
        requestedQuantity: 1,
        sawLine: seenToken !== null,
        currentQuantity: error.currentLine?.quantity ?? null,
        lastShown: null,
      });
      return {
        kind: 'conflict',
        message: view.kind === 'none' ? WOW_REFUSED_MESSAGE : view.message,
      };
    }
    if (error instanceof CartSendingError) return { kind: 'refused', message: WOW_SENDING_MESSAGE };
    if (error instanceof CartAlreadySentError)
      return { kind: 'refused', message: WOW_SENT_MESSAGE };
    if (error instanceof BasketServerError) return { kind: 'refused', message: error.message };
    if (error instanceof BasketHttpError && error.status < 500) {
      if (endsTheSession(error)) return { kind: 'sessionEnded' };
      if (error.status === 403) return { kind: 'forbidden' };
      return { kind: 'refused', message: WOW_REFUSED_MESSAGE };
    }
    // A network error, a 5xx or an unreadable success: the write may have landed. Never resend.
    return reportUncertain(sku, cart);
  }
  const line = productLine(basket, sku);
  // A written add always returns the line; without it the outcome is not known.
  return line === undefined ? reportUncertain(sku, cart) : { kind: 'added', line: lineOf(line) };
}

/**
 * Act on the WOW offer for `sku`: read the WOW order, then add 1 when the product has no line
 * there, or report the line it has (nothing written) so the person can choose "Add 1 more".
 */
export async function startWowAdd(
  sku: string,
  cart: CartTarget,
  stillCurrent: StillCurrent,
): Promise<WowAddResult> {
  let basket: BasketState;
  try {
    basket = await listBasket(cart);
  } catch (error) {
    // Nothing has been written yet. A refusal is not a failure to read: the cart is refused to
    // this session, so trying again cannot help until the offer is read again.
    if (error instanceof BasketHttpError && error.status === 403) {
      return endsTheSession(error) ? { kind: 'sessionEnded' } : { kind: 'forbidden' };
    }
    return { kind: 'unread' };
  }
  const existing = productLine(basket, sku);
  if (existing !== undefined && existing.token !== undefined) {
    return {
      kind: 'alreadyOnOrder',
      line: lineOf(existing),
      token: existing.token,
      revision: basket.revision,
    };
  }
  return writeWowLine(sku, cart, null, basket.revision, stillCurrent);
}

/** "Add 1 more" to the line the person was shown (`token`, at `revision`): it increments at the
 *  line's locked price. */
export function addOneMoreToWow(
  sku: string,
  cart: CartTarget,
  token: string,
  revision: number,
  stillCurrent: StillCurrent,
): Promise<WowAddResult> {
  return writeWowLine(sku, cart, token, revision, stillCurrent);
}

/** The line as words: "3 on your WOW order at €7.50" (the price left out when unknown). */
function lineText(line: WowOrderLine): string {
  const price = line.unitPrice === undefined ? '' : ` at ${formatMoney(line.unitPrice)}`;
  return `${line.quantity} on your WOW order${price}`;
}

/** What the person is told after an attempt. `null` for an add that landed (the caller shows
 *  its own confirmation) and for an ended session (the sign-out is what they see). */
export function wowAddMessage(result: WowAddResult): string | null {
  switch (result.kind) {
    case 'added':
      return null;
    case 'alreadyOnOrder':
      return `You already have ${lineText(result.line)}.`;
    case 'conflict':
    case 'refused':
      return result.message;
    case 'forbidden':
      return WOW_FORBIDDEN_MESSAGE;
    case 'sessionEnded':
      return null;
    case 'unread':
      return WOW_UNREAD_MESSAGE;
    case 'uncertain':
      if (result.line === undefined) {
        return "We couldn't confirm the add. Check your WOW order before trying again.";
      }
      return result.line === null
        ? "We couldn't confirm the add. Your WOW order doesn't have this product."
        : `We couldn't confirm the add. You now have ${lineText(result.line)}.`;
  }
}

/** Whether a result wrote (or may have written) to the WOW order, so the open-cart counts are
 *  refreshed. */
export function mayHaveWritten(result: WowAddResult): boolean {
  return result.kind === 'added' || result.kind === 'uncertain';
}

/** Whether the offer should be read again: the add was refused (the WOW cart included) or its
 *  outcome is unknown. Not after a failed read (`unread`: nothing was learnt, try again) or an
 *  ended session. */
export function rereadsTheOffer(result: WowAddResult): boolean {
  return result.kind === 'refused' || result.kind === 'forbidden' || result.kind === 'uncertain';
}

/** Whether the ordering choices should be read again in the background: the add wrote, or may
 *  have (the open-cart counts), or the WOW cart was refused (what the session is offered may have
 *  changed; a WOW entry no longer offered then leaves the page with no WOW context). */
export function refreshesTheChoices(result: WowAddResult): boolean {
  return mayHaveWritten(result) || result.kind === 'forbidden';
}
